<template>
  <v-container class="hyp-map-controls pa-3 ma-0">
    <!-- Points Control -->
    <div class="mb-4">
      <h3 class="pull-left">Points</h3>
      <!-- Last refinement, decided to not have feature for points toggle, keeping this for now -->
      <!-- <v-switch
        class="pull-right mt-0"
        v-model="mapPointsControlEnabled"
        @change="
          toggleMapControl('mapPointsControlEnabled', mapPointsControlEnabled)
        "
        hide-details
      ></v-switch> -->
      <div class="clearfix"></div>
    </div>

    <v-expansion-panels readonly accordion flat elevation-0>
      <v-expansion-panel v-for="section in pointsSections" :key="section.id">
        <v-expansion-panel-header hide-actions class="pa-2">
          <template>
            <div>
              <h4 class="hyp-map-controls-section-title pl-2 mt-1">
                {{ section.title }}
                <a
                  v-if="section.filterLinkPanel"
                  class="ml-5 caption"
                  @click="handleClickFilter(section)"
                  >Filter</a
                >
              </h4>
              <v-checkbox
                class="ma-0 pa-0 pull-right"
                v-model="section.visible"
                hide-details
                @change="handleChangePoints"
              ></v-checkbox>
            </div>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content eager>
          <component
            v-show="section.contentComp"
            v-bind:is="section.contentComp"
          ></component>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Overlays Control -->
    <div class="mt-10 mb-4">
      <h3 class="pull-left">Overlays</h3>
      <v-switch
        class="pull-right mt-0"
        v-model="mapOverlaysControlEnabled"
        @change="
          toggleMapControl(
            'mapOverlaysControlEnabled',
            mapOverlaysControlEnabled
          )
        "
        hide-details
      ></v-switch>
      <div class="clearfix"></div>
    </div>

    <!-- <OverlayOpacityBar class="mx-2"></OverlayOpacityBar> -->
    <v-radio-group
      hide-details
      class="pa-0 ma-0"
      v-model="mapOverlaysControlSelectedType"
      @change="handleChangeOverlays"
    >
      <v-expansion-panels accordion flat elevation-0>
        <v-expansion-panel
          v-for="section in overlaysSections"
          :key="section.id"
        >
          <v-expansion-panel-header hide-actions class="pa-2">
            <template v-slot:default="{ open }">
              <div>
                <v-icon>
                  <template v-if="open">mdi-chevron-up</template>
                  <template v-else>mdi-chevron-down</template>
                </v-icon>
                <h4 class="hyp-map-controls-section-title pl-2">
                  {{ section.title }}
                </h4>
                <v-radio
                  class="ma-0 pa-0 pull-right"
                  hide-details
                  :value="section"
                >
                </v-radio>
              </div>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content eager>
            <component
              v-if="section.contentComp"
              v-bind:is="section.contentComp"
            ></component>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-radio-group>

    <!-- Base Layer Control -->
    <div class="mt-10 mb-4">
      <h3>Base Layer</h3>
    </div>
    <BaseLayer :map-id="mapId" :options="baseLayerControlOptions"></BaseLayer>
  </v-container>
</template>

<script>
import lodashCloneDeep from "lodash/cloneDeep";

import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_MAP_CONTROLS,
  MODULE_PREFIX_HOME,
} from "@/store";
import {
  SAVE_PREFERENCES,
  GET_MAP_CONTROLS_PARAMS,
  GET_SELECTOR_PANEL,
} from "@/store/actions.type";

import {
  POINTS_SECTIONS_ENABLED_DEFAULT,
  OVERLAYS_SECTIONS_ENABLED_DEFAULT,
  OVERLAYS_SECTIONS_SELECTED_TYPE_DEFAULT,
  POINTS_SECTIONS,
  OVERLAYS_SECTIONS,
} from "@/common/constants/mapControls";
import { MAIN_MAP_ID } from "@/common/constants/maps";

import { populateDefaultPreferences as pulseDefaultPreferences } from "@/services/grids/stability.service";
import { populateDefaultPreferences as riskRatingDefaultPreferences } from "@/services/controlRisks/risks.service";

export default {
  name: "MapControls",
  components: {
    BaseLayer: () => import("@/components/map/BaseLayer"),
    // OverlayOpacityBar: () =>
    //   import("@/components/mapControls/pulse/PulseOpacityBar"),
    MapControlsOverlayRiskRatings: () =>
      import("@/components/mapControls/MapControlsOverlayRiskRatings"),
    MapControlsOverlayPulse: () =>
      import("@/components/mapControls/MapControlsOverlayPulse"),
    MapControlsOverlayPowerOutage: () =>
      import("@/components/mapControls/MapControlsOverlayPowerOutage"),
    MapControlsPointsHotspots: () =>
      import("@/components/mapControls/MapControlsPointsHotspots"),
    MapControlsPointsMyIncidents: () =>
      import("@/components/mapControls/MapControlsPointsMyIncidents"),
    MapControlsPointsAssets: () =>
      import("@/components/mapControls/MapControlsPointsAssets"),
  },
  data: () => ({
    mapPointsControlEnabledDefault: POINTS_SECTIONS_ENABLED_DEFAULT,
    mapOverlaysControlEnabledDefault: OVERLAYS_SECTIONS_ENABLED_DEFAULT,
    mapOverlaysControlSelectedTypeDefault: OVERLAYS_SECTIONS_SELECTED_TYPE_DEFAULT,
    overlaysSections: OVERLAYS_SECTIONS,
    pointsSections: POINTS_SECTIONS,
    mapPointsControlEnabled: false,
    mapOverlaysControlEnabled: false,
    mapOverlaysControlSelectedType: null,
  }),
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      preferences: MODULE_PREFIX_USER + "preferences",
      mapControlParams: MODULE_PREFIX_MAP_CONTROLS + "params",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
    }),
    mapId() {
      return MAIN_MAP_ID;
    },
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    baseLayerControlOptions() {
      return {
        showInline: true,
        position: "relative",
        btnSize: 36,
        bottom: null,
        right: null,
      };
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      // get pref/defaults
      this.getPrefs();
      // Set map controls from pref/defaults
      let mapControlParams = lodashCloneDeep(this.mapControlParams);
      mapControlParams.mapPointsControlEnabled = this.mapPointsControlEnabled;
      mapControlParams.mapOverlaysControlEnabled = this.mapOverlaysControlEnabled;
      mapControlParams.mapOverlaysControlSelectedType = this.mapOverlaysControlSelectedType?.id;
      mapControlParams.mapPointsControlSelectedTypes = this.pointsSections
        .filter((section) => section.visible)
        .map((section) => {
          return section.id;
        });

      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_PARAMS,
        mapControlParams
      );
      this.updatePref();
    },
    getPrefs() {
      this.mapPointsControlEnabled =
        this.preferences?.mapControls?.mapPointsControlEnabled ??
        this.mapPointsControlEnabledDefault;
      this.mapOverlaysControlEnabled =
        this.preferences?.mapControls?.mapOverlaysControlEnabled ??
        this.mapOverlaysControlEnabledDefault;

      const mapOverlaysControlSelectedTypeId =
        this.preferences?.mapControls?.mapOverlaysControlSelectedType ??
        this.mapOverlaysControlSelectedTypeDefault;
      this.mapOverlaysControlSelectedType = this.overlaysSections.find(
        (s) => s.id === mapOverlaysControlSelectedTypeId
      );

      this.pointsSections = this.preferences?.mapControls
        ?.mapPointsControlSelectedTypes
        ? this.pointsSections.map((section) => {
            section.visible =
              this.preferences?.mapControls?.mapPointsControlSelectedTypes.includes(
                section.id
              ) ?? section.visibleDefault;
            return section;
          })
        : this.pointsSections;
    },
    updatePref() {
      let preferences = lodashCloneDeep(this.preferences);
      preferences.mapControls = preferences.mapControls || {};
      preferences.mapControls.mapPointsControlEnabled = this.mapPointsControlEnabled;
      preferences.mapControls.mapOverlaysControlEnabled = this.mapOverlaysControlEnabled;
      preferences.mapControls.mapOverlaysControlSelectedType = this.mapOverlaysControlSelectedType?.id;
      preferences.mapControls.mapPointsControlSelectedTypes = this.pointsSections
        .filter((section) => section.visible)
        .map((section) => {
          return section.id;
        });
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
    },
    toggleMapControl(type, visible) {
      let mapControlParams = lodashCloneDeep(this.mapControlParams);
      mapControlParams[type] = visible;
      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_PARAMS,
        mapControlParams
      );
      this.updatePref();
    },
    async handleChangePoints() {
      let mapControlParams = lodashCloneDeep(this.mapControlParams);
      mapControlParams.mapPointsControlSelectedTypes = this.pointsSections
        .filter((section) => section.visible)
        .map((section) => {
          return section.id;
        });
      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_PARAMS,
        mapControlParams
      );
      this.updatePref();
    },
    async handleChangeOverlays() {
      let mapControlParams = lodashCloneDeep(this.mapControlParams);
      mapControlParams.mapOverlaysControlSelectedType = this.mapOverlaysControlSelectedType.id;
      this.$store.dispatch(
        MODULE_PREFIX_MAP_CONTROLS + GET_MAP_CONTROLS_PARAMS,
        mapControlParams
      );
      this.updatePref();
    },
    handleClickFilter(section) {
      const selectorPanel = lodashCloneDeep(this.selectorPanel);
      selectorPanel.selected = section.filterLinkPanel;
      selectorPanel.visible = true;
      selectorPanel.isSelected = true;
      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
        selectorPanel
      );
    },
  },
};
</script>

<style scoped>
.hyp-map-controls {
  width: 350px;
  max-height: calc(100vh - 100px) !important;
  overflow-y: auto;
}
.hyp-map-controls .v-expansion-panel {
  border-bottom: 1px solid #dddddd;
}
.hyp-map-controls .v-expansion-panels {
  border-top: 1px solid #dddddd;
}
.hyp-map-controls-section-title {
  display: inline-block;
}
</style>
